import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { PLATS, NM, COST_FIELDS } from './constants';
import { ml } from './format';
import { calcPlatform } from './calc';
import { getPlatformState, getCostState, getDealTerms } from './stateFactory';

const HEADER_FILL = { fgColor: { rgb: '0F1525' } };
const SUBHEADER_FILL = { fgColor: { rgb: '141A2E' } };
const TOTAL_FILL = { fgColor: { rgb: '192035' } };
const GRAND_FILL = { fgColor: { rgb: '1E2640' } };

const HEADER_FONT = { name: 'Arial', bold: true, color: { rgb: 'F0F3FA' }, sz: 9 };
const BODY_FONT   = { name: 'Arial', sz: 9 };
const LABEL_FONT  = { name: 'Arial', bold: true, sz: 9, color: { rgb: '8993A6' } };
const TOTAL_FONT  = { name: 'Arial', bold: true, sz: 9, color: { rgb: 'F0F3FA' } };

const USD_FMT   = '$#,##0;($#,##0);-';
const PCT_FMT   = '0.0%;(0.0%);-';
const NUM_FMT   = '#,##0;(#,##0);-';

function monthHeaders() {
  return Array.from({ length: NM }, (_, i) => ({
    v: ml(i), t: 's',
    s: { font: HEADER_FONT, fill: HEADER_FILL, alignment: { horizontal: 'right' } }
  }));
}

function labelCell(v) {
  return { v, t: 's', s: { font: LABEL_FONT, fill: SUBHEADER_FILL } };
}

function numRow(label, values, opts = {}) {
  const row = [labelCell(label)];
  values.forEach(v => {
    const n = typeof v === 'number' ? v : null;
    row.push({
      v: n ?? '',
      t: n !== null ? 'n' : 's',
      z: n !== null ? USD_FMT : undefined,
      s: {
        font: opts.bold ? TOTAL_FONT : BODY_FONT,
        fill: opts.fill || {},
        alignment: { horizontal: 'right' }
      }
    });
  });
  return row;
}

function strRow(label, values, opts = {}) {
  const row = [labelCell(label)];
  values.forEach(v => {
    row.push({
      v: v ?? '—', t: 's',
      s: { font: BODY_FONT, fill: opts.fill || {}, alignment: { horizontal: 'right' } }
    });
  });
  return row;
}

function spacerRow(cols) {
  return Array(cols + 1).fill({ v: '', t: 's' });
}

/**
 * Build a worksheet for a single game.
 */
function buildGameSheet(gameName, PS, CS, DT) {
  const aoa = []; // array of arrays (rows)

  // ── Title
  aoa.push([{ v: gameName.toUpperCase() + ' — 36-Month Publishing Forecast', t: 's',
    s: { font: { name: 'Arial', bold: true, sz: 12, color: { rgb: 'F0F3FA' } },
         fill: { fgColor: { rgb: '060911' } } } }]);
  aoa.push(spacerRow(NM));

  const enabledPlats = PLATS.filter(p => getPlatformState(PS, gameName, p.key).enabled);

  // ── Per-platform sections
  enabledPlats.forEach(p => {
    const st = getPlatformState(PS, gameName, p.key);
    const rc = calcPlatform(p.key, st);
    const isOther = p.key === 'other';

    // Platform header row
    const hRow = [{
      v: p.label.toUpperCase(), t: 's',
      s: { font: { name: 'Arial', bold: true, sz: 10, color: { rgb: 'C8CFDE' } },
           fill: { fgColor: { rgb: '0F1525' } } }
    }, ...monthHeaders()];
    aoa.push(hRow);

    // Units
    const unitsRaw = st.units.map(v => parseFloat(v) || 0);
    aoa.push(numRow('Base Game Units', unitsRaw, { fmt: NUM_FMT }));

    // RRP
    aoa.push(strRow('RRP', rc.rrpDisplay));

    // Regional pct
    const regRaw = st.regionalPct.map(v => (parseFloat(v) || 0) / 100);
    aoa.push(numRow('Regional Pricing %', regRaw, { fmt: PCT_FMT }));

    // Discount pct
    const discRaw = st.discountPct.map(v => (parseFloat(v) || 0) / 100);
    aoa.push(numRow('Discount %', discRaw, { fmt: PCT_FMT }));

    // Gross revenue
    aoa.push(numRow('Gross Revenue', rc.grossRaw.map(v => v ?? 0), { fill: SUBHEADER_FILL }));

    if (isOther) {
      aoa.push(numRow('Platform Deal', st.platformDeal.map(v => parseFloat(v) || 0)));
      aoa.push(numRow(st.customRow1Label || 'Custom Row 1', st.customRow1.map(v => parseFloat(v) || 0)));
      aoa.push(numRow(st.customRow2Label || 'Custom Row 2', st.customRow2.map(v => parseFloat(v) || 0)));
    } else {
      aoa.push(strRow('Platform Fee %', rc.feePct));
    }

    // Net revenue
    aoa.push(numRow('Net Revenue', rc.netRaw.map(v => v ?? 0), { bold: true, fill: TOTAL_FILL }));
    aoa.push(spacerRow(NM));
  });

  // ── Revenue Summary
  if (enabledPlats.length > 0) {
    const tg = Array(NM).fill(0), tn = Array(NM).fill(0);
    enabledPlats.forEach(p => {
      const rc = calcPlatform(p.key, getPlatformState(PS, gameName, p.key));
      rc.grossRaw.forEach((v, i) => { if (v !== null) tg[i] += v; });
      rc.netRaw.forEach((v, i)   => { if (v !== null) tn[i] += v; });
    });
    aoa.push([{
      v: 'REVENUE SUMMARY — ALL PLATFORMS', t: 's',
      s: { font: { name: 'Arial', bold: true, sz: 9, color: { rgb: '8993A6' } },
           fill: { fgColor: { rgb: '0F1525' } } }
    }, ...monthHeaders()]);
    aoa.push(numRow('Total Gross Revenue', tg, { fill: SUBHEADER_FILL }));
    aoa.push(numRow('Total Net Revenue',   tn, { bold: true, fill: GRAND_FILL }));
    aoa.push(spacerRow(NM));
  }

  // ── Costs
  const costs = getCostState(CS, gameName);
  ['recoup', 'nonRecoup'].forEach(sec => {
    const label = sec === 'recoup' ? 'RECOUPABLE COSTS' : 'NON-RECOUPABLE COSTS';
    aoa.push([{
      v: label, t: 's',
      s: { font: { name: 'Arial', bold: true, sz: 9, color: { rgb: '8993A6' } },
           fill: { fgColor: { rgb: '0F1525' } } }
    }, ...monthHeaders()]);

    const totals = Array(NM).fill(0);
    COST_FIELDS.forEach(({ key, label: fl }) => {
      const vals = costs[sec][key].map(v => parseFloat(v) || 0);
      vals.forEach((v, i) => { totals[i] += v; });
      aoa.push(numRow(fl, vals));
    });
    aoa.push(numRow(`Total ${label}`, totals, { bold: true, fill: GRAND_FILL }));
    aoa.push(spacerRow(NM));
  });

  // ── Deal Terms sidebar (two-column, after monthly data)
  const dt = getDealTerms(DT, gameName);
  aoa.push([{ v: 'DEAL TERMS', t: 's', s: { font: HEADER_FONT, fill: HEADER_FILL } }]);
  aoa.push([labelCell('Dev Advance Currency'), { v: dt.devAdvanceCurrency || 'USD', t: 's', s: { font: BODY_FONT } }]);
  aoa.push([labelCell('Dev Advance Amount'),   { v: parseFloat(dt.devAdvanceAmt) || 0, t: 'n', z: USD_FMT, s: { font: BODY_FONT } }]);
  aoa.push([labelCell('Pub Costs Budget'),      { v: parseFloat(dt.pubCostsBudget) || 0, t: 'n', z: USD_FMT, s: { font: BODY_FONT } }]);
  aoa.push([labelCell('Marketing Budget'),      { v: parseFloat(dt.marketingBudget) || 0, t: 'n', z: USD_FMT, s: { font: BODY_FONT } }]);
  aoa.push([labelCell('Royalty Pre-Recoup'),    { v: (parseFloat(dt.royaltyPreRecoup) || 0) / 100, t: 'n', z: PCT_FMT, s: { font: BODY_FONT } }]);
  aoa.push([labelCell('Royalty Post-Recoup'),   { v: (parseFloat(dt.royaltyPostRecoup) || 0) / 100, t: 'n', z: PCT_FMT, s: { font: BODY_FONT } }]);

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Column widths: label col wide, month cols narrow
  ws['!cols'] = [{ wch: 28 }, ...Array(NM).fill({ wch: 11 })];

  return ws;
}

/**
 * Build the P&L summary sheet across all games.
 */
function buildPnLSheet(games, PS) {
  const aoa = [];
  aoa.push([{
    v: 'GROSS REVENUE BY GAME — 36-MONTH FORECAST', t: 's',
    s: { font: { name: 'Arial', bold: true, sz: 12, color: { rgb: 'F0F3FA' } },
         fill: { fgColor: { rgb: '060911' } } }
  }]);
  aoa.push(spacerRow(NM));

  const headerRow = [{ v: 'Game', t: 's', s: { font: HEADER_FONT, fill: HEADER_FILL } }, ...monthHeaders()];
  aoa.push(headerRow);

  const grand = Array(NM).fill(0);
  games.forEach(g => {
    const gt = Array(NM).fill(0);
    PLATS.forEach(p => {
      const st = PS[g]?.[p.key];
      if (!st?.enabled) return;
      calcPlatform(p.key, st).grossRaw.forEach((v, i) => {
        if (v !== null) { gt[i] += v; grand[i] += v; }
      });
    });
    aoa.push(numRow(g, gt));
  });

  aoa.push(spacerRow(NM));
  aoa.push(numRow('TOTAL GROSS REVENUE', grand, { bold: true, fill: GRAND_FILL }));

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 24 }, ...Array(NM).fill({ wch: 11 })];
  return ws;
}

/**
 * Main export entry point.
 * Exports a workbook with a P&L summary sheet + one sheet per game.
 */
export function exportToExcel(games, PS, CS, DT, Q1 = null) {
  const wb = XLSX.utils.book_new();

  // P&L summary
  const pnlWs = buildPnLSheet(games, PS);
  XLSX.utils.book_append_sheet(wb, pnlWs, 'P&L Summary');

  // Per-game sheets
  games.forEach(g => {
    const ws = buildGameSheet(g, PS, CS, DT);
    // Sheet names max 31 chars, strip special chars
    const sheetName = g.replace(/[|&;$%@"<>()+,]/g, '').slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  // Q1 comparison sheet if available
  if (Q1) {
    const compAoa = buildComparisonSheet(games, PS, Q1.PS || {});
    const compWs = XLSX.utils.aoa_to_sheet(compAoa);
    compWs['!cols'] = [{ wch: 28 }, ...Array(NM).fill({ wch: 11 })];
    XLSX.utils.book_append_sheet(wb, compWs, 'Q1 vs Latest');
  }

  const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
  const blob = new Blob([buf], { type: 'application/octet-stream' });
  const date = new Date().toISOString().slice(0, 10);
  saveAs(blob, `publishing-forecast-${date}.xlsx`);
}

function buildComparisonSheet(games, livePS, rfPS) {
  const aoa = [];
  aoa.push([{ v: 'NET REVENUE — LATEST vs Q1 REFORECAST', t: 's',
    s: { font: { name: 'Arial', bold: true, sz: 12, color: { rgb: 'F0F3FA' } },
         fill: { fgColor: { rgb: '060911' } } } }]);
  aoa.push(spacerRow(NM));

  games.forEach(g => {
    // Game header
    aoa.push([{ v: g.toUpperCase(), t: 's',
      s: { font: { name: 'Arial', bold: true, sz: 10, color: { rgb: 'C8CFDE' } },
           fill: { fgColor: { rgb: '0F1525' } } }
    }, ...monthHeaders()]);

    PLATS.forEach(p => {
      const lst = livePS[g]?.[p.key];
      const rst = rfPS[g]?.[p.key];
      if (!lst?.enabled && !rst?.enabled) return;

      const lv = lst?.enabled ? calcPlatform(p.key, lst).netRaw : Array(NM).fill(null);
      const rv = rst?.enabled ? calcPlatform(p.key, rst).netRaw : Array(NM).fill(null);
      const variance = lv.map((l, i) => {
        const a = l ?? 0, b = rv[i] ?? 0;
        return l === null && rv[i] === null ? null : a - b;
      });

      aoa.push(numRow(`${p.label} · Latest`,     lv.map(v => v ?? 0)));
      aoa.push(numRow(`${p.label} · Reforecast`, rv.map(v => v ?? 0)));
      aoa.push(numRow(`${p.label} · Variance`,   variance.map(v => v ?? 0), { bold: true, fill: TOTAL_FILL }));
      aoa.push(spacerRow(NM));
    });
  });

  return aoa;
}
